/** 画像の遅延取得とLRUキャッシュ（仕様書 §7.7）。 */

import { getDb } from '../db/schema';
import { graph } from '../auth/GraphClient';
import { mediaPath, strokesPath } from './paths';
import type { MediaRef, MonthKey, StrokeDoc } from '../domain/types';

const MEDIA_CACHE_KEY = 'nj.mediaCacheLimit';
const DEFAULT_LIMIT = 500 * 1024 * 1024;
/** 直近3か月分は LRU の削除対象にしない。 */
const KEEP_MONTHS = 3;

export function getCacheLimit(): number {
  const v = Number(localStorage.getItem(MEDIA_CACHE_KEY));
  return Number.isFinite(v) && v > 0 ? v : DEFAULT_LIMIT;
}

export function setCacheLimit(bytes: number): void {
  localStorage.setItem(MEDIA_CACHE_KEY, String(bytes));
}

/**
 * ローカルに無ければ OneDrive から取得してキャッシュする。
 * オフラインや未同期の場合は null を返し、呼び出し側がプレースホルダを出す。
 */
export async function ensureMediaBlob(
  media: MediaRef,
  month: MonthKey,
  entryId: string,
): Promise<Blob | null> {
  const db = await getDb();
  const rec = await db.get('media', media.id);
  if (rec) {
    await db.put('media', { ...rec, lastAccessAt: Date.now() });
    return rec.blob;
  }
  if (!navigator.onLine) return null;

  let blob: Blob | null;
  try {
    blob = await graph.getBlob(mediaPath(month, media.file));
  } catch {
    return null;
  }
  if (!blob) return null;

  await db.put('media', {
    id: media.id,
    entryId,
    month,
    file: media.file,
    mime: media.mime,
    blob,
    bytes: blob.size,
    lastAccessAt: Date.now(),
    _uploaded: true,
  });

  if (media.strokesFile) {
    try {
      const doc = await graph.getJson<StrokeDoc>(strokesPath(month, media.strokesFile));
      if (doc) {
        await db.put('strokes', {
          id: media.id,
          entryId,
          month,
          data: doc.data,
          _uploaded: true,
        });
      }
    } catch {
      /* ストロークが取れなくても画像は表示できる */
    }
  }

  void evictIfNeeded();
  return blob;
}

/**
 * 手書きの再編集用ストロークを用意する。
 * ローカルに無ければ OneDrive から取りに行く。
 * これをしないと、別の端末では「再編集できません」になってしまう。
 */
export async function ensureStrokes(
  media: MediaRef,
  month: MonthKey,
  entryId: string,
): Promise<StrokeDoc | undefined> {
  if (media.type !== 'drawing') return undefined;
  const db = await getDb();
  const local = await db.get('strokes', media.id);
  if (local) return local.data;

  const file = media.strokesFile ?? `${media.id}.strokes.json`;
  if (!navigator.onLine) return undefined;
  try {
    const doc = await graph.getJson<StrokeDoc>(strokesPath(month, file));
    if (!doc) return undefined;
    await db.put('strokes', {
      id: media.id,
      entryId,
      month,
      data: doc.data,
      _uploaded: true,
    });
    return doc.data;
  } catch {
    return undefined;
  }
}

/** 上限を超えたら、古いものから Blob を捨てる。未アップロードのものは対象外。 */
export async function evictIfNeeded(): Promise<void> {
  const db = await getDb();
  const limit = getCacheLimit();
  if (limit === Infinity) return;

  const all = await db.getAll('media');
  const total = all.reduce((n, m) => n + m.bytes, 0);
  if (total <= limit) return;

  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - KEEP_MONTHS);
  const keepFrom = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}`;

  const candidates = all
    .filter((m) => m._uploaded && m.month < keepFrom)
    .sort((a, b) => a.lastAccessAt - b.lastAccessAt);

  let freed = 0;
  for (const m of candidates) {
    if (total - freed <= limit) break;
    await db.delete('media', m.id);
    freed += m.bytes;
  }
}

export async function clearMediaCache(): Promise<number> {
  const db = await getDb();
  const all = await db.getAll('media');
  let freed = 0;
  for (const m of all) {
    if (!m._uploaded) continue; // 未送信のものは消さない
    await db.delete('media', m.id);
    freed += m.bytes;
  }
  return freed;
}
